import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormSchema, IssuedKitItem, MobilisationEvent, Station } from '@hcc/types';
import { api } from '../lib/api';

/** The configured handover checklist for a station, or null. */
export interface HandoverFormInfo {
  form: { id: string; title: string; station: Station } | null;
  version: { id: string; version: number; schema: FormSchema } | null;
}

export function useHandoverForm(station: Station) {
  return useQuery({
    queryKey: ['kit-handover-form', station],
    queryFn: () => api<HandoverFormInfo>(`/api/kit-handover/form?station=${station}`),
    staleTime: 5 * 60 * 1000,
  });
}

export interface HandoverSubmitInput {
  station: Station;
  form_id: string;
  values: Record<string, unknown>;
  files: Array<{ field_key: string; filename: string; base64: string }>;
  stored_files: Array<{ field_key: string; name: string; key: string; content_type?: string }>;
  items: IssuedKitItem[];
  kit_notes?: string;
  signature_png: string;
  client_event_id?: string;
}

/**
 * Submit a completed handover: kit items + checklist answers + the driver
 * signature, written atomically server-side (KitIssued + KitFormSubmitted +
 * the submission row).
 */
export function useSubmitHandover(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HandoverSubmitInput) =>
      api<{ submission_id: string; events: MobilisationEvent[] }>(
        `/api/kit-handover/people/${personId}/submit`,
        { method: 'POST', body: JSON.stringify(input) },
      ),
    onSuccess: () => {
      // Same refresh set as a plain kit issue: the station lists and this
      // person's outstanding-kit seed both change.
      qc.invalidateQueries({ queryKey: ['people'] });
      qc.invalidateQueries({ queryKey: ['station-kit', personId] });
    },
  });
}

type VideoInit =
  | { mode: 's3'; uploadUrl: string; headers: Record<string, string>; key: string; content_type: string }
  | { mode: 'direct'; uploadUrl: string };

/**
 * Stream a walk-around video to storage from the desk (presigned PUT on S3,
 * raw endpoint in dev) and return the ref for the submit body's stored_files.
 */
export async function uploadHandoverVideo(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<{ name: string; key: string; content_type?: string }> {
  const init = await api<VideoInit>('/api/kit-handover/video-uploads', {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, size: file.size }),
  });
  const put = (url: string, headers: Record<string, string>) =>
    new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.responseText);
        else reject(new Error(`Upload failed (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
  if (init.mode === 's3') {
    await put(init.uploadUrl, init.headers);
    return { name: file.name, key: init.key, content_type: init.content_type };
  }
  const body = await put(init.uploadUrl, { 'Content-Type': 'application/octet-stream' });
  const parsed = JSON.parse(body) as { key: string; content_type?: string };
  return { name: file.name, key: parsed.key, content_type: parsed.content_type };
}
